import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Plus, Trash2, Pencil, FilePlus, FileText, FileDown, HardHat, History, CheckCircle2, ArrowRight, UserPlus, AlertCircle, X, Calendar as CalendarIcon, Zap, Timer } from "lucide-react";
import { formatMoney, formatDuration } from "@/lib/format";
import { useNowTick } from "@/lib/useNowTick";
import { Checkbox } from "@/components/ui/checkbox";
import AttachmentsSection from "@/components/AttachmentsSection";
import TransactionsTable from "@/components/TransactionsTable";
import FinishDialog from "@/components/FinishDialog";
import DynamicDetailsForm, { buildDefaults } from "@/components/DynamicDetailsForm";
import DynamicDetailsView, { hasAnyValue } from "@/components/DynamicDetailsView";
import { toast } from "sonner";

const STATUS = {
  active: { label: "Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  completed: { label: "Tamamlandı", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  cancelled: { label: "İptal", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};
const PHASE_STATUS = { pending: "Bekliyor", in_progress: "Devam Ediyor", done: "Tamamlandı" };

const PRIORITY_LABEL = { normal: "Normal", high: "Yüksek", urgent: "Acil" };
const PRIORITY_CLS = {
  normal: "bg-zinc-100 text-zinc-600 border-zinc-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  urgent: "bg-red-50 text-red-700 border-red-200",
};

function fmtDT(s) {
  if (!s) return "-";
  try { return new Date(s).toLocaleString("tr-TR"); } catch { return s; }
}

function isDateOverdue(d) {
  if (!d) return false;
  const today = new Date(); today.setHours(0,0,0,0);
  return new Date(d) < today;
}

function AssignmentBadge({ a }) {
  const dates = a.scheduled_dates || [];
  const overdue = dates.length > 0 && dates.some(d => isDateOverdue(d)) && a.status !== "done";
  return (
    <div className="p-3 rounded-md border border-zinc-200 bg-white text-sm" data-testid={`assignment-${a.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <HardHat size={12} className="text-amber-600" />
        <span className="font-medium">{a.user_name}</span>
        <span className="text-zinc-400">·</span>
        <span className="inline-block px-2 py-0.5 rounded text-xs bg-zinc-100 text-zinc-700">{a.stage}</span>
        <span className={`inline-block px-2 py-0.5 rounded text-xs border ${PRIORITY_CLS[a.priority] || PRIORITY_CLS.normal}`}>
          {a.priority === "urgent" && <Zap size={10} className="inline mr-0.5"/>}
          {PRIORITY_LABEL[a.priority] || "Normal"}
        </span>
        {overdue && (
          <span className="inline-block px-2 py-0.5 rounded text-xs bg-red-50 text-red-700 border border-red-200">
            <AlertCircle size={10} className="inline mr-0.5"/>Gecikmiş
          </span>
        )}
        {a.status === "done" && (
          <span className="inline-block px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">Tamamlandı</span>
        )}
      </div>
      {dates.length > 0 && (
        <div className="mt-1 text-xs text-zinc-500 flex items-center gap-1 flex-wrap">
          <CalendarIcon size={11} />
          {dates.map(d => <span key={d} className={isDateOverdue(d) && a.status !== "done" ? "text-red-600" : ""}>{d}</span>).reduce((acc,el,i,arr)=>i<arr.length-1?[...acc,el,<span key={"s"+i}>·</span>]:[...acc,el],[])}
        </div>
      )}
      {a.notes && <div className="mt-1 text-xs text-zinc-600">{a.notes}</div>}
    </div>
  );
}

export default function ProjectDetail() {
  useNowTick(60);
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const backTo = location.state?.returnTo || "/projeler";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [phases, setPhases] = useState([]);
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [settings, setSettings] = useState({ project_phases: [], manufacturing_stages: [] });
  const [categories, setCategories] = useState([]);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [projDelOpen, setProjDelOpen] = useState(false);

  const [phaseOpen, setPhaseOpen] = useState(false);
  const [phaseForm, setPhaseForm] = useState({ id: null, name: "", start_date: "", end_date: "", status: "pending", notes: "" });
  const [phaseDelOpen, setPhaseDelOpen] = useState(false);
  const [phaseDelTarget, setPhaseDelTarget] = useState(null);

  const [itemOpen, setItemOpen] = useState(false);
  const [itemForm, setItemForm] = useState({ id: null, name: "", material: "", color: "", model: "", quantity: 1, unit: "adet", unit_price: 0, manufacturing_stage: "", notes: "", category_id: null, details: {} });
  const [itemDelOpen, setItemDelOpen] = useState(false);
  const [itemDelTarget, setItemDelTarget] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [pdfLoading, setPdfLoading] = useState(false);

  // Takeover / finish state
  const [takeoverOpen, setTakeoverOpen] = useState(false);
  const [takeoverItem, setTakeoverItem] = useState(null);
  const [takeoverStage, setTakeoverStage] = useState("");
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishItem, setFinishItem] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsItem, setLogsItem] = useState(null);
  const [logs, setLogs] = useState([]);

  // Item detail (read-only) modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailAssignments, setDetailAssignments] = useState([]);
  const openDetail = async (it) => {
    setDetailItem(it);
    setDetailOpen(true);
    try {
      const [rl, ra] = await Promise.all([
        api.get(`/projects/${id}/items/${it.id}/logs`),
        api.get(`/projects/${id}/items/${it.id}/assignments`),
      ]);
      setDetailLogs(rl.data);
      setDetailAssignments(ra.data);
    } catch { setDetailLogs([]); setDetailAssignments([]); }
  };

  // Assignment dialog (admin only)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignItem, setAssignItem] = useState(null);
  const [assignExisting, setAssignExisting] = useState([]);
  const [assignForm, setAssignForm] = useState({ id: null, user_id: "", stage: "", priority: "normal", scheduled_dates: [], notes: "" });
  const [assignDateInput, setAssignDateInput] = useState("");
  const [ustas, setUstas] = useState([]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/users").then(r => setUstas(r.data.filter(u => u.role === "usta"))).catch(()=>{});
    }
  }, [isAdmin]);

  const openAssign = async (it) => {
    setAssignItem(it);
    setAssignForm({ id: null, user_id: "", stage: it.manufacturing_stage || "", priority: "normal", scheduled_dates: [], notes: "" });
    setAssignDateInput("");
    setAssignOpen(true);
    try {
      const r = await api.get(`/projects/${id}/items/${it.id}/assignments`);
      setAssignExisting(r.data);
    } catch { setAssignExisting([]); }
  };
  const addAssignDate = () => {
    if (!assignDateInput) return;
    if (assignForm.scheduled_dates.includes(assignDateInput)) return;
    setAssignForm({ ...assignForm, scheduled_dates: [...assignForm.scheduled_dates, assignDateInput].sort() });
    setAssignDateInput("");
  };
  const removeAssignDate = (d) => setAssignForm({ ...assignForm, scheduled_dates: assignForm.scheduled_dates.filter(x => x !== d) });
  const saveAssign = async () => {
    if (!assignForm.user_id) { toast.error("Bir usta seçin"); return; }
    if (!assignForm.stage) { toast.error("Aşama seçin"); return; }
    const body = {
      user_id: assignForm.user_id,
      stage: assignForm.stage,
      priority: assignForm.priority,
      scheduled_dates: assignForm.scheduled_dates,
      notes: assignForm.notes || "",
    };
    try {
      if (assignForm.id) {
        await api.put(`/projects/${id}/items/${assignItem.id}/assignments/${assignForm.id}`, body);
        toast.success("Görev güncellendi");
      } else {
        await api.post(`/projects/${id}/items/${assignItem.id}/assignments`, body);
        toast.success("Görev atandı");
      }
      // refresh list in dialog
      const r = await api.get(`/projects/${id}/items/${assignItem.id}/assignments`);
      setAssignExisting(r.data);
      setAssignForm({ id: null, user_id: "", stage: assignItem?.manufacturing_stage || "", priority: "normal", scheduled_dates: [], notes: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    }
  };
  const editAssign = (a) => setAssignForm({
    id: a.id, user_id: a.user_id, stage: a.stage,
    priority: a.priority || "normal", scheduled_dates: a.scheduled_dates || [], notes: a.notes || "",
  });
  const deleteAssign = async (a) => {
    try {
      await api.delete(`/projects/${id}/items/${assignItem.id}/assignments/${a.id}`);
      const r = await api.get(`/projects/${id}/items/${assignItem.id}/assignments`);
      setAssignExisting(r.data);
      toast.success("Görev silindi");
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const loadAll = async () => {
    const reqs = [
      api.get(`/projects/${id}`),
      api.get(`/projects/${id}/phases`),
      api.get(`/projects/${id}/items`),
      api.get(`/settings`),
      api.get(`/product-categories`),
    ];
    if (isAdmin) {
      reqs.push(
        api.get(`/projects/${id}/summary`),
        api.get(`/sales-invoices`, { params: { project_id: id } }),
        api.get(`/transactions`, { params: { project_id: id } }),
      );
    }
    try {
      const results = await Promise.all(reqs);
      setProject(results[0].data);
      setPhases(results[1].data);
      setItems(results[2].data);
      setSettings(results[3].data);
      setCategories(results[4].data);
      if (isAdmin) {
        setSummary(results[5].data);
        setInvoices(results[6].data);
        setTransactions(results[7].data);
      }
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Bu projeye erişim yetkiniz yok");
        nav("/projeler");
      } else {
        toast.error("Yüklenemedi");
      }
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id, isAdmin]);

  const saveProject = async () => {
    try {
      const body = {
        customer_id: editForm.customer_id,
        name: editForm.name,
        status: editForm.status,
        start_date: editForm.start_date ? new Date(editForm.start_date).toISOString() : null,
        end_date: editForm.end_date ? new Date(editForm.end_date).toISOString() : null,
        notes: editForm.notes || "",
      };
      await api.put(`/projects/${id}`, body);
      toast.success("Proje güncellendi");
      setEditOpen(false);
      loadAll();
    } catch { toast.error("Güncellenemedi"); }
  };

  const delProject = async () => {
    await api.delete(`/projects/${id}`);
    toast.success("Proje silindi");
    nav(backTo);
  };

  // Phases (admin only)
  const openPhaseNew = () => { setPhaseForm({ id: null, name: "", start_date: "", end_date: "", status: "pending", notes: "" }); setPhaseOpen(true); };
  const openPhaseEdit = (p) => { setPhaseForm({
    id: p.id, name: p.name,
    start_date: p.start_date?.slice(0,10) || "",
    end_date: p.end_date?.slice(0,10) || "",
    status: p.status || "pending", notes: p.notes || "",
  }); setPhaseOpen(true); };
  const savePhase = async () => {
    if (!phaseForm.name) { toast.error("Faz adı zorunlu"); return; }
    const body = {
      name: phaseForm.name,
      start_date: phaseForm.start_date ? new Date(phaseForm.start_date).toISOString() : null,
      end_date: phaseForm.end_date ? new Date(phaseForm.end_date).toISOString() : null,
      status: phaseForm.status, notes: phaseForm.notes || "",
    };
    if (phaseForm.id) await api.put(`/projects/${id}/phases/${phaseForm.id}`, body);
    else await api.post(`/projects/${id}/phases`, body);
    toast.success("Kaydedildi");
    setPhaseOpen(false);
    loadAll();
  };
  const delPhase = async () => {
    await api.delete(`/projects/${id}/phases/${phaseDelTarget.id}`);
    setPhaseDelOpen(false);
    loadAll();
  };

  // Items (admin CRUD)
  const openItemNew = () => { setItemForm({ id: null, name: "", material: "", color: "", model: "", quantity: 1, unit: "adet", unit_price: 0, manufacturing_stage: "", notes: "", category_id: null, details: {} }); setItemOpen(true); };
  const openItemEdit = (it) => { setItemForm({ ...it, quantity: it.quantity || 1, unit_price: it.unit_price || 0, category_id: it.category_id || null, details: it.details || {} }); setItemOpen(true); };
  const onItemCategoryChange = (cid) => {
    const cat = categories.find(c => c.id === cid);
    // Yeni kategori seçilince (mevcut detay boşsa) varsayılan değerleri doldur
    const emptyDetails = !itemForm.details || Object.keys(itemForm.details).length === 0;
    const details = (cat && emptyDetails) ? buildDefaults(cat.fields || []) : (itemForm.details || {});
    setItemForm({ ...itemForm, category_id: cid, details });
  };
  const saveItem = async () => {
    if (!itemForm.name) { toast.error("Ürün adı zorunlu"); return; }
    const body = {
      name: itemForm.name, material: itemForm.material || "", color: itemForm.color || "",
      model: itemForm.model || "", quantity: Number(itemForm.quantity || 1), unit: itemForm.unit || "adet",
      unit_price: Number(itemForm.unit_price || 0),
      manufacturing_stage: itemForm.manufacturing_stage || "", notes: itemForm.notes || "",
      category_id: itemForm.category_id || null, details: itemForm.details || {},
    };
    if (itemForm.id) await api.put(`/projects/${id}/items/${itemForm.id}`, body);
    else await api.post(`/projects/${id}/items`, body);
    toast.success("Kaydedildi");
    setItemOpen(false);
    loadAll();
  };
  const delItem = async () => {
    await api.delete(`/projects/${id}/items/${itemDelTarget.id}`);
    setSelectedItemIds(prev => { const n = new Set(prev); n.delete(itemDelTarget.id); return n; });
    setItemDelOpen(false);
    loadAll();
  };

  // Takeover / Finish
  const openTakeover = (it) => {
    setTakeoverItem(it);
    setTakeoverStage(it.manufacturing_stage || "");
    setTakeoverOpen(true);
  };
  const doTakeover = async () => {
    if (!takeoverStage) { toast.error("Aşama seçin"); return; }
    try {
      await api.post(`/projects/${id}/items/${takeoverItem.id}/takeover`, { stage: takeoverStage });
      toast.success(`"${takeoverStage}" aşaması devralındı`);
      setTakeoverOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Devralma başarısız");
    }
  };

  const myActiveTakeover = (it) =>
    (it.active_takeovers || []).find(t => t.user_id === user?.id);

  const openFinish = (it) => {
    setFinishItem(it);
    setFinishOpen(true);
  };

  const openLogs = async (it) => {
    setLogsItem(it);
    setLogsOpen(true);
    try {
      const r = await api.get(`/projects/${id}/items/${it.id}/logs`);
      setLogs(r.data);
    } catch { setLogs([]); }
  };

  // PDF & selection
  const toggleItemSel = (iid) => setSelectedItemIds(prev => {
    const n = new Set(prev);
    if (n.has(iid)) n.delete(iid); else n.add(iid);
    return n;
  });
  const toggleAllItems = () => setSelectedItemIds(prev =>
    prev.size === items.length ? new Set() : new Set(items.map(i => i.id))
  );
  const generateItemsPdf = async () => {
    if (selectedItemIds.size === 0) return;
    setPdfLoading(true);
    try {
      const r = await api.post(`/projects/${id}/items/pdf`, { item_ids: Array.from(selectedItemIds) }, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("PDF oluşturulamadı"); }
    finally { setPdfLoading(false); }
  };

  const invoiceFromSelected = () => {
    if (selectedItemIds.size === 0) return;
    const chosen = items.filter(it => selectedItemIds.has(it.id));
    // Build invoice line items from project items. VAT / discount stay at
    // form defaults (vat_rate = 0). Enrich display name with material/color/
    // model so the invoice PDF reads well.
    const prefillItems = chosen.map(it => {
      const extras = [it.material, it.color, it.model].filter(x => x && String(x).trim()).join(" / ");
      const name = extras ? `${it.name} — ${extras}` : it.name;
      return {
        product_id: null,
        project_item_id: it.id,
        name,
        quantity: Number(it.quantity || 1),
        unit: it.unit || "adet",
        unit_price: Number(it.unit_price || 0),
        vat_rate: 0,
      };
    });
    nav(`/satis-faturalari/yeni?entity_id=${project.customer_id}&project_id=${id}`, {
      state: { prefillItems, returnTo: `/projeler/${id}` },
    });
  };

  const newInvoice = () => nav(`/satis-faturalari/yeni?entity_id=${project.customer_id}&project_id=${id}`);

  if (!project) return <div className="text-zinc-500">Yükleniyor...</div>;

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav(backTo)} data-testid="project-back-button"><ArrowLeft size={16} /></Button>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Proje</div>
          <h1 className="text-3xl mt-1 flex items-center gap-3">
            {project.name}
            <span className={`inline-block px-2 py-0.5 rounded text-xs border ${STATUS[project.status]?.cls || ""}`}>{STATUS[project.status]?.label}</span>
          </h1>
          <div className="text-sm text-zinc-500 mt-1">
            Müşteri: {isAdmin
              ? <Link to={`/musteriler/${project.customer_id}`} className="hover:underline">{project.customer_name}</Link>
              : <span>{project.customer_name}</span>}
          </div>
        </div>
        {isAdmin && (
          <>
            <Button data-testid="project-edit-btn" variant="outline" onClick={()=>{setEditForm({ ...project, start_date: project.start_date?.slice(0,10) || "", end_date: project.end_date?.slice(0,10) || "" }); setEditOpen(true);}}><Pencil size={14} className="mr-1"/>Düzenle</Button>
            <Button data-testid="project-delete-btn" variant="outline" onClick={()=>setProjDelOpen(true)}>Sil</Button>
          </>
        )}
      </div>

      {isAdmin && summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="p-4 border border-zinc-200 shadow-none">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Satış Toplamı</div>
            <div className="mt-2 text-xl number">{formatMoney(summary.sales_total, "TRY")}</div>
            <div className="text-xs text-zinc-500">{summary.sales_count} fatura</div>
          </Card>
          <Card className="p-4 border border-zinc-200 shadow-none">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Tahsilat</div>
            <div className="mt-2 text-xl number pos">{formatMoney(summary.payments_received, "TRY")}</div>
          </Card>
          <Card className="p-4 border border-zinc-200 shadow-none">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Giderler (Kasa)</div>
            <div className="mt-2 text-xl number neg">{formatMoney(summary.payments_paid, "TRY")}</div>
          </Card>
          <Card className="p-4 border border-zinc-200 shadow-none">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Fazlar</div>
            <div className="mt-2 text-xl">{summary.phase_count}</div>
          </Card>
          <Card className="p-4 border border-zinc-200 shadow-none">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Ürünler</div>
            <div className="mt-2 text-xl">{summary.item_count}</div>
          </Card>
        </div>
      )}

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">Genel</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">Takvim</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Ürünler</TabsTrigger>
          {isAdmin && <TabsTrigger value="invoices" data-testid="tab-project-invoices">Faturalar</TabsTrigger>}
          {isAdmin && <TabsTrigger value="transactions" data-testid="tab-project-transactions">Hareketler</TabsTrigger>}
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <Card className="p-5 border border-zinc-200 shadow-none space-y-2 text-sm">
            <div><span className="text-zinc-500">Başlangıç: </span>{project.start_date?.slice(0,10) || "-"}</div>
            <div><span className="text-zinc-500">Bitiş: </span>{project.end_date?.slice(0,10) || "-"}</div>
            {project.notes && <div><span className="text-zinc-500">Notlar: </span>{project.notes}</div>}
          </Card>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4 space-y-3">
          {isAdmin && (
            <div className="flex justify-end">
              <Button data-testid="new-phase-button" onClick={openPhaseNew} className="bg-zinc-900 hover:bg-zinc-800"><Plus size={14} className="mr-1"/>Faz Ekle</Button>
            </div>
          )}
          <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
            <table className="acc">
              <thead><tr><th>Faz</th><th>Başlangıç</th><th>Bitiş</th><th>Durum</th>{isAdmin && <th style={{width:100}}></th>}</tr></thead>
              <tbody>
                {phases.length===0 && <tr><td colSpan={isAdmin?5:4} className="text-center text-zinc-500 py-8">Henüz faz yok</td></tr>}
                {phases.map(p => (
                  <tr key={p.id} data-testid={`phase-row-${p.id}`}>
                    <td className="font-medium">{p.name}</td>
                    <td>{p.start_date?.slice(0,10) || "-"}</td>
                    <td>{p.end_date?.slice(0,10) || "-"}</td>
                    <td>{PHASE_STATUS[p.status] || p.status}</td>
                    {isAdmin && (
                      <td className="flex gap-1">
                        <button onClick={()=>openPhaseEdit(p)} className="text-zinc-400 hover:text-zinc-900"><Pencil size={14}/></button>
                        <button onClick={()=>{ setPhaseDelTarget(p); setPhaseDelOpen(true); }} className="text-zinc-400 hover:text-red-600"><Trash2 size={14}/></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="products" className="mt-4 space-y-3">
          <div className="flex justify-between items-center gap-2">
            <div className="text-sm text-zinc-500">
              {isAdmin
                ? (selectedItemIds.size > 0
                    ? `${selectedItemIds.size} ürün seçili`
                    : "PDF veya fatura için ürünlerin başındaki kutucukları işaretleyin")
                : "Devral butonu ile bir ürünün aşamasını devralabilirsiniz."}
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button data-testid="items-pdf-button" onClick={generateItemsPdf} disabled={selectedItemIds.size === 0 || pdfLoading} variant="outline" className="disabled:opacity-40">
                  <FileDown size={14} className="mr-1"/>PDF Oluştur ({selectedItemIds.size})
                </Button>
                <Button data-testid="items-invoice-button" onClick={invoiceFromSelected} disabled={selectedItemIds.size === 0} variant="outline" className="disabled:opacity-40">
                  <FileText size={14} className="mr-1"/>Fatura Oluştur ({selectedItemIds.size})
                </Button>
                <Button data-testid="new-item-button" onClick={openItemNew} className="bg-zinc-900 hover:bg-zinc-800"><Plus size={14} className="mr-1"/>Ürün Ekle</Button>
              </div>
            )}
          </div>
          <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
            <table className="acc">
              <thead><tr>
                {isAdmin && (
                  <th style={{width:36}}>
                    <Checkbox data-testid="items-select-all" checked={items.length > 0 && selectedItemIds.size === items.length} onCheckedChange={toggleAllItems} />
                  </th>
                )}
                <th>Ad</th><th>Malzeme</th><th>Renk</th><th>Model</th>
                <th className="num">Miktar</th><th>Birim</th>
                {isAdmin && <th className="num">Birim Fiyat</th>}
                {isAdmin && <th className="num">Toplam</th>}
                <th>İmalat Aşaması</th>
                <th>Aktif Devralanlar</th>
                <th style={{width:160}}></th>
              </tr></thead>
              <tbody>
                {items.length===0 && <tr><td colSpan={20} className="text-center text-zinc-500 py-8">Henüz ürün yok</td></tr>}
                {items.map(it => {
                  const price = Number(it.unit_price || 0);
                  const qty = Number(it.quantity || 0);
                  const rowTotal = price * qty;
                  const mine = myActiveTakeover(it);
                  const activeUsers = (it.active_takeovers || []).map(t => {
                    const dur = formatDuration(t.started_at, null);
                    return `${t.user_name} (${t.stage} · ${dur})`;
                  }).join(", ");
                  return (
                    <tr key={it.id} data-testid={`item-row-${it.id}`} className="cursor-pointer hover:bg-zinc-50" onClick={() => openDetail(it)}>
                      {isAdmin && (
                        <td onClick={(e)=>e.stopPropagation()}>
                          <Checkbox data-testid={`item-check-${it.id}`} checked={selectedItemIds.has(it.id)} onCheckedChange={()=>toggleItemSel(it.id)} />
                        </td>
                      )}
                      <td className="font-medium">
                        <div className="flex items-center gap-2">
                          <span>{it.name}</span>
                          {(it.invoiced_in || []).length > 0 && (
                            <span
                              data-testid={`item-invoiced-badge-${it.id}`}
                              title={`Faturalandı: ${it.invoiced_in.map(iv => iv.number).join(", ")}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200"
                              onClick={(e) => { e.stopPropagation(); const first = it.invoiced_in[0]; if (first?.id) nav(`/satis-faturalari/${first.id}`); }}
                            >
                              <FileText size={10} />Faturalandı
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="text-zinc-600">{it.material || "-"}</td>
                      <td className="text-zinc-600">{it.color || "-"}</td>
                      <td className="text-zinc-600">{it.model || "-"}</td>
                      <td className="num">{qty}</td>
                      <td className="text-zinc-600">{it.unit}</td>
                      {isAdmin && <td className="num">{formatMoney(price, "TRY")}</td>}
                      {isAdmin && <td className="num">{formatMoney(rowTotal, "TRY")}</td>}
                      <td><span className="inline-block px-2 py-0.5 rounded text-xs bg-zinc-100 text-zinc-700">{it.manufacturing_stage || "-"}</span></td>
                      <td className="text-xs text-zinc-600">{activeUsers || "-"}</td>
                      <td className="flex gap-1 items-center flex-wrap" onClick={(e)=>e.stopPropagation()}>
                        {project.status === "active" && !mine && (
                          <Button data-testid={`takeover-${it.id}`} size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={()=>openTakeover(it)}>
                            <HardHat size={12} className="mr-1"/>Devral
                          </Button>
                        )}
                        {project.status === "active" && mine && (
                          <Button data-testid={`finish-${it.id}`} size="sm" className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={()=>openFinish(it)}>
                            <CheckCircle2 size={12} className="mr-1"/>İşi Bitirdim
                          </Button>
                        )}
                        <button data-testid={`logs-${it.id}`} onClick={()=>openLogs(it)} className="text-zinc-400 hover:text-zinc-900" title="İşlem Geçmişi"><History size={14}/></button>
                        {isAdmin && <button data-testid={`assign-${it.id}`} onClick={()=>openAssign(it)} className="text-zinc-400 hover:text-zinc-900" title="Görev Ata"><UserPlus size={14}/></button>}
                        {isAdmin && <button onClick={()=>openItemEdit(it)} className="text-zinc-400 hover:text-zinc-900"><Pencil size={14}/></button>}
                        {isAdmin && <button onClick={()=>{ setItemDelTarget(it); setItemDelOpen(true); }} className="text-zinc-400 hover:text-red-600"><Trash2 size={14}/></button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="invoices" className="mt-4 space-y-3">
            <div className="flex justify-end">
              <Button data-testid="new-project-invoice-button" onClick={newInvoice} className="bg-zinc-900 hover:bg-zinc-800"><FilePlus size={14} className="mr-1"/>Fatura Oluştur</Button>
            </div>
            <div className="bg-white rounded-md border border-zinc-200 overflow-hidden">
              <table className="acc">
                <thead><tr><th>No</th><th>Tarih</th><th>Müşteri</th><th className="num">Toplam</th><th></th></tr></thead>
                <tbody>
                  {invoices.length===0 && <tr><td colSpan={5} className="text-center text-zinc-500 py-8">Fatura yok</td></tr>}
                  {invoices.map(inv => (
                    <tr key={inv.id} data-testid={`project-invoice-row-${inv.id}`} onClick={()=>nav(`/satis-faturalari/${inv.id}`)} className="cursor-pointer">
                      <td className="font-medium">{inv.invoice_number}</td>
                      <td className="text-zinc-600">{inv.date?.slice(0,10)}</td>
                      <td className="text-zinc-600">{inv.entity_name}</td>
                      <td className="num">{formatMoney(inv.total, inv.currency)}</td>
                      <td><FileText size={14} className="text-zinc-400" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="transactions" className="mt-4">
            <TransactionsTable items={transactions} onChanged={loadAll} />
          </TabsContent>
        )}
      </Tabs>

      {/* Edit project (admin) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="project-edit-dialog">
          <DialogHeader><DialogTitle>Projeyi Düzenle</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Proje Adı</Label><Input value={editForm.name} onChange={(e)=>setEditForm({...editForm, name: e.target.value})} /></div>
              <div><Label>Başlangıç</Label><Input type="date" value={editForm.start_date} onChange={(e)=>setEditForm({...editForm, start_date: e.target.value})} /></div>
              <div><Label>Bitiş</Label><Input type="date" value={editForm.end_date} onChange={(e)=>setEditForm({...editForm, end_date: e.target.value})} /></div>
              <div className="col-span-2">
                <Label>Durum</Label>
                <Select value={editForm.status} onValueChange={(v)=>setEditForm({...editForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="completed">Tamamlandı</SelectItem>
                    <SelectItem value="cancelled">İptal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Notlar</Label><Textarea rows={2} value={editForm.notes || ""} onChange={(e)=>setEditForm({...editForm, notes: e.target.value})} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setEditOpen(false)}>İptal</Button>
            <Button onClick={saveProject} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete project confirm */}
      <AlertDialog open={projDelOpen} onOpenChange={setProjDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Proje silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>İlişkili faturaların bağı kaldırılır. Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-project" onClick={delProject} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase form (admin) */}
      <Dialog open={phaseOpen} onOpenChange={setPhaseOpen}>
        <DialogContent data-testid="phase-dialog">
          <DialogHeader><DialogTitle>{phaseForm.id ? "Fazı Düzenle" : "Yeni Faz"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Faz Adı</Label>
              <Select value={phaseForm.name} onValueChange={(v)=>setPhaseForm({...phaseForm, name: v})}>
                <SelectTrigger data-testid="phase-name-select"><SelectValue placeholder="Faz seçin veya yazın" /></SelectTrigger>
                <SelectContent>{(settings.project_phases || []).map(v=><SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="phase-name-input" className="mt-1" placeholder="Veya özel ad yazın" value={phaseForm.name} onChange={(e)=>setPhaseForm({...phaseForm, name: e.target.value})} />
            </div>
            <div><Label>Başlangıç</Label><Input type="date" value={phaseForm.start_date} onChange={(e)=>setPhaseForm({...phaseForm, start_date: e.target.value})} /></div>
            <div><Label>Bitiş</Label><Input type="date" value={phaseForm.end_date} onChange={(e)=>setPhaseForm({...phaseForm, end_date: e.target.value})} /></div>
            <div className="col-span-2">
              <Label>Durum</Label>
              <Select value={phaseForm.status} onValueChange={(v)=>setPhaseForm({...phaseForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Bekliyor</SelectItem>
                  <SelectItem value="in_progress">Devam Ediyor</SelectItem>
                  <SelectItem value="done">Tamamlandı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notlar</Label><Textarea rows={2} value={phaseForm.notes} onChange={(e)=>setPhaseForm({...phaseForm, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setPhaseOpen(false)}>İptal</Button>
            <Button data-testid="phase-save-button" onClick={savePhase} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={phaseDelOpen} onOpenChange={setPhaseDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Faz silinsin mi?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={delPhase} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Item form (admin) */}
      <Dialog open={itemOpen} onOpenChange={setItemOpen}>
        <DialogContent data-testid="item-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{itemForm.id ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Ad</Label><Input data-testid="item-name-input" value={itemForm.name} onChange={(e)=>setItemForm({...itemForm, name: e.target.value})} /></div>
            <div><Label>Malzeme</Label><Input value={itemForm.material} onChange={(e)=>setItemForm({...itemForm, material: e.target.value})} /></div>
            <div><Label>Renk</Label><Input value={itemForm.color} onChange={(e)=>setItemForm({...itemForm, color: e.target.value})} /></div>
            <div><Label>Model</Label><Input value={itemForm.model} onChange={(e)=>setItemForm({...itemForm, model: e.target.value})} /></div>
            <div><Label>Miktar</Label><Input type="number" step="0.01" value={itemForm.quantity} onChange={(e)=>setItemForm({...itemForm, quantity: e.target.value})} /></div>
            <div><Label>Birim</Label><Input value={itemForm.unit} onChange={(e)=>setItemForm({...itemForm, unit: e.target.value})} /></div>
            <div><Label>Birim Fiyat (TRY)</Label><Input data-testid="item-price-input" type="number" step="0.01" value={itemForm.unit_price} onChange={(e)=>setItemForm({...itemForm, unit_price: e.target.value})} /></div>
            <div>
              <Label>İmalat Aşaması</Label>
              <Select value={itemForm.manufacturing_stage || "none"} onValueChange={(v)=>setItemForm({...itemForm, manufacturing_stage: v === "none" ? "" : v})}>
                <SelectTrigger data-testid="item-stage-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {(settings.manufacturing_stages || []).map(v=><SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Ürün Kategorisi</Label>
              <Select value={itemForm.category_id || "none"} onValueChange={(v)=>onItemCategoryChange(v === "none" ? null : v)}>
                <SelectTrigger data-testid="item-category-select"><SelectValue placeholder="Kategori seçin (opsiyonel)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Kategori yok —</SelectItem>
                  {categories.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(() => {
              const cat = categories.find(c => c.id === itemForm.category_id);
              if (!cat || (cat.fields || []).length === 0) return null;
              return (
                <div className="col-span-2 border border-zinc-200 rounded-md p-3 bg-zinc-50/40" data-testid="item-details-section">
                  <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{cat.name} — İmalat Detayları <span className="lowercase tracking-normal">(tümü opsiyonel)</span></div>
                  <DynamicDetailsForm
                    fields={cat.fields}
                    value={itemForm.details || {}}
                    onChange={(details)=>setItemForm({...itemForm, details})}
                    testIdPrefix="item-detail"
                  />
                </div>
              );
            })()}
            <div className="col-span-2"><Label>Notlar</Label><Textarea rows={2} value={itemForm.notes} onChange={(e)=>setItemForm({...itemForm, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setItemOpen(false)}>İptal</Button>
            <Button data-testid="item-save-button" onClick={saveItem} className="bg-zinc-900 hover:bg-zinc-800">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={itemDelOpen} onOpenChange={setItemDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Ürün silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Ürüne ait işlem geçmişi de silinir.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={delItem} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Takeover dialog */}
      <Dialog open={takeoverOpen} onOpenChange={setTakeoverOpen}>
        <DialogContent data-testid="takeover-dialog">
          <DialogHeader>
            <DialogTitle>Aşamayı Devral</DialogTitle>
            <DialogDescription>
              <span className="font-medium text-zinc-800">{takeoverItem?.name}</span> için hangi imalat aşamasını başlatıyorsunuz?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Aşama</Label>
              <Select value={takeoverStage} onValueChange={setTakeoverStage}>
                <SelectTrigger data-testid="takeover-stage-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {(settings.manufacturing_stages || []).map(v=><SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setTakeoverOpen(false)}>İptal</Button>
            <Button data-testid="takeover-confirm" onClick={doTakeover} className="bg-zinc-900 hover:bg-zinc-800">
              <HardHat size={14} className="mr-1"/>Devral
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finish dialog */}
      <FinishDialog
        open={finishOpen}
        onOpenChange={setFinishOpen}
        projectId={id}
        itemId={finishItem?.id}
        itemName={finishItem?.name}
        stages={settings.manufacturing_stages || []}
        onFinished={loadAll}
      />

      {/* Item detail (read-only) */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent data-testid="item-detail-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ürün Detayı</DialogTitle>
            <DialogDescription>
              <span className="font-medium text-zinc-800">{detailItem?.name}</span> — sadece görüntüleme. Düzenlemek için satırdaki kalem butonunu kullanın.
            </DialogDescription>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Ad</div>
                  <div className="font-medium mt-0.5">{detailItem.name}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Mevcut İmalat Aşaması</div>
                  <div className="mt-0.5">
                    {detailItem.manufacturing_stage
                      ? <span className="inline-block px-2 py-0.5 rounded text-xs bg-zinc-100 text-zinc-700">{detailItem.manufacturing_stage}</span>
                      : <span className="text-zinc-400">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Malzeme</div>
                  <div className="mt-0.5">{detailItem.material || <span className="text-zinc-400">—</span>}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Renk</div>
                  <div className="mt-0.5">{detailItem.color || <span className="text-zinc-400">—</span>}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Model</div>
                  <div className="mt-0.5">{detailItem.model || <span className="text-zinc-400">—</span>}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Miktar</div>
                  <div className="mt-0.5 number">{Number(detailItem.quantity || 0)} {detailItem.unit || ""}</div>
                </div>
                {isAdmin && (
                  <>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500">Birim Fiyat</div>
                      <div className="mt-0.5 number">{formatMoney(Number(detailItem.unit_price || 0), "TRY")}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-zinc-500">Toplam</div>
                      <div className="mt-0.5 number font-medium">
                        {formatMoney(Number(detailItem.unit_price || 0) * Number(detailItem.quantity || 0), "TRY")}
                      </div>
                    </div>
                  </>
                )}
                {detailItem.notes && (
                  <div className="col-span-2">
                    <div className="text-xs uppercase tracking-widest text-zinc-500">Notlar</div>
                    <div className="mt-0.5 whitespace-pre-wrap">{detailItem.notes}</div>
                  </div>
                )}
              </div>

              {(() => {
                const cat = categories.find(c => c.id === detailItem.category_id);
                if (!cat || (cat.fields || []).length === 0) return null;
                const filled = hasAnyValue(cat.fields, detailItem.details);
                return (
                  <div data-testid="item-detail-details">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{cat.name} — İmalat Detayları</div>
                    {filled
                      ? <DynamicDetailsView fields={cat.fields} value={detailItem.details || {}} testIdPrefix="item-detail-view" />
                      : <div className="text-sm text-zinc-500">Detay girilmemiş.</div>}
                  </div>
                );
              })()}

              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Atanmış Görevler</div>
                {detailAssignments.length === 0 ? (
                  <div className="text-sm text-zinc-500">Bu ürüne atanmış bir görev yok.</div>
                ) : (
                  <div className="space-y-2">
                    {detailAssignments.map(a => <AssignmentBadge key={a.id} a={a} />)}
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Aktif Devralanlar</div>
                {(detailItem.active_takeovers || []).length === 0 ? (
                  <div className="text-sm text-zinc-500">Şu anda ürünü devralan yok.</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {detailItem.active_takeovers.map(t => (
                      <div key={t.id} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs" data-testid={`detail-active-${t.id}`}>
                        <HardHat size={12} className="text-amber-600" />
                        <span className="font-medium">{t.user_name}</span>
                        <span className="text-zinc-500">·</span>
                        <span>{t.stage}</span>
                        <span className="text-zinc-500">·</span>
                        <span className="inline-flex items-center gap-0.5 text-blue-700 font-medium" title={`Başlangıç: ${fmtDT(t.started_at)}`}>
                          <Timer size={10}/>{formatDuration(t.started_at, null)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">İşlem Geçmişi ({detailLogs.length})</div>
                {detailLogs.length === 0 ? (
                  <div className="text-sm text-zinc-500">Henüz kayıt yok.</div>
                ) : (
                  <div className="max-h-56 overflow-auto rounded-md border border-zinc-200">
                    <table className="acc">
                      <thead><tr><th>Usta</th><th>Aşama</th><th>Başlangıç</th><th>Bitiş</th><th>Süre</th><th>Durum</th></tr></thead>
                      <tbody>
                        {detailLogs.map(l => (
                          <tr key={l.id}>
                            <td className="font-medium">{l.user_name}</td>
                            <td><span className="inline-block px-2 py-0.5 rounded text-xs bg-zinc-100 text-zinc-700">{l.stage}</span></td>
                            <td className="text-xs text-zinc-600">{fmtDT(l.started_at)}</td>
                            <td className="text-xs text-zinc-600">{fmtDT(l.finished_at)}</td>
                            <td className="text-xs" data-testid={`detail-log-duration-${l.id}`}>
                              {l.finished_at
                                ? <span className="text-zinc-700">{formatDuration(l.started_at, l.finished_at)}</span>
                                : <span className="text-blue-700 font-medium">{formatDuration(l.started_at, null)}</span>}
                            </td>
                            <td>
                              {!l.finished_at
                                ? <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">Devam ediyor</span>
                                : l.completed
                                  ? <span className="inline-block px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">Ürünle işim bitti</span>
                                  : <span className="inline-block px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">Yeni aşamaya geçti</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <AttachmentsSection
                ownerKind="project_item"
                ownerId={detailItem.id}
                title="Fotoğraf & Dosyalar"
                accept="image/*,application/pdf"
                testIdPrefix="item-attachments"
              />
            </div>
          )}
          <DialogFooter>
            <Button data-testid="item-detail-close" variant="ghost" onClick={()=>setDetailOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Logs dialog */}
      <Dialog open={logsOpen} onOpenChange={setLogsOpen}>
        <DialogContent data-testid="logs-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>İşlem Geçmişi</DialogTitle>
            <DialogDescription>
              <span className="font-medium text-zinc-800">{logsItem?.name}</span> — hangi usta ne zaman hangi aşamayı yaptı.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-auto -mx-6 px-6">
            {logs.length === 0 ? (
              <div className="text-center text-zinc-500 py-8 text-sm">Henüz kayıt yok.</div>
            ) : (
              <table className="acc">
                <thead><tr><th>Usta</th><th>Aşama</th><th>Başlangıç</th><th>Bitiş</th><th>Süre</th><th>Durum</th></tr></thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id}>
                      <td className="font-medium">{l.user_name}</td>
                      <td>
                        <span className="inline-block px-2 py-0.5 rounded text-xs bg-zinc-100 text-zinc-700">{l.stage}</span>
                      </td>
                      <td className="text-xs text-zinc-600">{fmtDT(l.started_at)}</td>
                      <td className="text-xs text-zinc-600">{fmtDT(l.finished_at)}</td>
                      <td className="text-xs" data-testid={`logs-duration-${l.id}`}>
                        {l.finished_at
                          ? <span className="text-zinc-700">{formatDuration(l.started_at, l.finished_at)}</span>
                          : <span className="text-blue-700 font-medium">{formatDuration(l.started_at, null)}</span>}
                      </td>
                      <td>
                        {!l.finished_at
                          ? <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">Devam ediyor</span>
                          : l.completed
                            ? <span className="inline-block px-2 py-0.5 rounded text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">Ürünle işim bitti</span>
                            : <span className="inline-block px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">Yeni aşamaya geçti</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={()=>setLogsOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign task dialog (admin) */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Görev Ata — {assignItem?.name}</DialogTitle>
            <DialogDescription>
              Ustanın hangi aşamayı yapacağını, öncelik ve tarihleri belirleyin. Tarihler opsiyoneldir.
            </DialogDescription>
          </DialogHeader>

          {/* Existing assignments */}
          {assignExisting.length > 0 && (
            <div className="space-y-2 max-h-56 overflow-auto pr-1">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Mevcut Görevler</div>
              {assignExisting.map(a => (
                <div key={a.id} className="flex items-start gap-2">
                  <div className="flex-1"><AssignmentBadge a={a} /></div>
                  <button onClick={()=>editAssign(a)} data-testid={`edit-assign-${a.id}`} className="text-zinc-400 hover:text-zinc-900 mt-2"><Pencil size={14}/></button>
                  <button onClick={()=>deleteAssign(a)} data-testid={`delete-assign-${a.id}`} className="text-zinc-400 hover:text-red-600 mt-2"><Trash2 size={14}/></button>
                </div>
              ))}
              <div className="border-t border-zinc-200 pt-3 mt-3"></div>
            </div>
          )}

          {/* Form */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 text-xs uppercase tracking-widest text-zinc-500">
              {assignForm.id ? "Görevi Düzenle" : "Yeni Görev"}
            </div>
            <div>
              <Label>Usta</Label>
              <Select value={assignForm.user_id} onValueChange={(v)=>setAssignForm({...assignForm, user_id: v})}>
                <SelectTrigger data-testid="assign-user-select"><SelectValue placeholder="Usta seçin" /></SelectTrigger>
                <SelectContent>
                  {ustas.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500">Önce Kullanıcılar sayfasından usta ekleyin</div>}
                  {ustas.map(u=><SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Aşama</Label>
              <Select value={assignForm.stage} onValueChange={(v)=>setAssignForm({...assignForm, stage: v})}>
                <SelectTrigger data-testid="assign-stage-select"><SelectValue placeholder="Aşama seçin" /></SelectTrigger>
                <SelectContent>
                  {(settings.manufacturing_stages || []).map(v=><SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Öncelik</Label>
              <Select value={assignForm.priority} onValueChange={(v)=>setAssignForm({...assignForm, priority: v})}>
                <SelectTrigger data-testid="assign-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">Yüksek</SelectItem>
                  <SelectItem value="urgent">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tarih Ekle (opsiyonel)</Label>
              <div className="flex gap-1">
                <Input data-testid="assign-date-input" type="date" value={assignDateInput} onChange={(e)=>setAssignDateInput(e.target.value)} />
                <Button type="button" variant="outline" onClick={addAssignDate} data-testid="assign-date-add"><Plus size={14}/></Button>
              </div>
            </div>
            <div className="col-span-2">
              {assignForm.scheduled_dates.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {assignForm.scheduled_dates.map(d => (
                    <div key={d} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-zinc-100 border border-zinc-200 text-xs" data-testid={`assign-date-chip-${d}`}>
                      <CalendarIcon size={11} />{d}
                      <button onClick={()=>removeAssignDate(d)} className="p-0.5 rounded-full hover:bg-zinc-200"><X size={10}/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <Label>Notlar</Label>
              <Textarea rows={2} value={assignForm.notes} onChange={(e)=>setAssignForm({...assignForm, notes: e.target.value})} placeholder="Detay veya talimatlar..." />
            </div>
          </div>
          <DialogFooter>
            {assignForm.id && <Button variant="ghost" onClick={()=>setAssignForm({ id: null, user_id: "", stage: assignItem?.manufacturing_stage || "", priority: "normal", scheduled_dates: [], notes: "" })}>Yeni Görev</Button>}
            <Button variant="ghost" onClick={()=>setAssignOpen(false)}>Kapat</Button>
            <Button data-testid="assign-save-button" onClick={saveAssign} className="bg-zinc-900 hover:bg-zinc-800">
              {assignForm.id ? "Güncelle" : "Ata"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
